import React from 'react';
import PropTypes from 'prop-types';
import event from '../../../utils/analytics/event';
import { LinkContainer, SocialButtonContainer, StackedContainer, FaviconContainer, LogoFavicon, CopyrightText, CustomizedFooter, SocialButtons, Link, OutsideLink, SocialLogoContainer } from './StyledFooter.styles';
/**
 * Styled footer component as part of the Phase One product requirements. This footer allows for social buttons, any links and the product's logo & copyright.
 *
 * This footer is NOT themeable; however, it uses BodyText, AppLink, ExternalLink, Logo and SocialPageButton which can be themed.
 *
 * To add whichever social buttons are relevant to your product, you'll need to create a social buttons object that you'll pass in for the `socialButtons` prop. This object should look like this:
 *
 * ```
 * const socialButtons = {
 *   facebook: '',
 *   instagram: '',
 *   medium: '',
 *   pinterest: '',
 *   twitter: '',
 *   shop: '',
 * }
 * ```
 *
 * And you can take out any of the platforms that you do not wish to include. The social buttons come from the `SocialPageButton` component in Pantheon; feel free to read [this](http://localhost:56010/?path=/docs/components-widgets-socialpagebuttons--all-buttons) documentation to learn more. To customize these social buttons, you can theme them and wrap this component in a ThemeProvider.
 *
 * For the product links in the middle, you can add any number of internal or external links through an array passed into the `links` prop. The array should hold multiple objects in the form of {to: '', title:''} where `to` holds the route and `title` holds menu item label. Each item defaults to being an applink (inside of the application); however, if you would like to use an external link then add `external: true` to the object at hand.
 *
 * By default, the piwik events logged are:
 *
 * - `['click', 'footer-item', <LINK TITLE>]` for any links
 * - `['click', 'footer-item', <SOCIAL BUTTON-page-view>]` for any social buttons
 *
 * To customize any of the piwik events fired, you can pass in an object for the `piwikEvents` prop. The object should be structured as such:
 *
 *```js
 * const piwikEvents = {
 *   link: [],
 *   socialButton: []
 * }
 * ```
 *
 * ## Example Usage
 *
 * ```js
 * import { StyledFooter } from '@minerva-analytics/pantheon';
 *
 * const SocialButtons = {
 *   facebook: 'https://www.facebook.com/QuickScopeNews',
 *   instagram: 'https://www.instagram.com/quickscopenews',
 *   medium: 'https://medium.com/@thequickscope',
 * }
 *
 * const links = [
 *   {
 *     to: '/policy',
 *     title: 'Privacy Policy',
 *   },
 *   {
 *     to: '/terms',
 *     title: 'Terms of Service',
 *   },
 *   {
 *     to: 'https://www.google.com',
 *     title: 'RSS Feed',
 *     external: true,
 *   },
 * ];
 *
 * <StyledFooter
 *   productName="Quickscope"
 *   productLogo="https://storage.googleapis.com/quickscope/assets/quickscope_favicon.png"
 *   textColor="white"
 *   font="Arial"
 *   backgroundColor="navy"
 *   socialButtons={SocialButtons}
 *   links={links}
 * />
 * ```
 */

var StyledFooter = function StyledFooter(props) {
  var hasSocialButtons = Boolean(props.socialButtons);

  var linksEl = function linksEl(links, piwikEvents) {
    return /*#__PURE__*/React.createElement(LinkContainer, null, links.map(function (link) {
      return link.external || link.useFullInternalLink ? /*#__PURE__*/React.createElement(OutsideLink, {
        key: link.title,
        href: link.to,
        target: link.useFullInternalLink ? '_self' : '_blank',
        onClick: function onClick() {
          event(piwikEvents && piwikEvents.link ? piwikEvents.link : ['click', 'footer-item', link.title]);
          window.scroll(0, 0);
        }
      }, link.title) : /*#__PURE__*/React.createElement(Link, {
        key: link.title,
        to: link.to,
        onClick: function onClick() {
          event(piwikEvents && piwikEvents.link ? piwikEvents.link : ['click', 'footer-item', link.title]);
          window.scroll(0, 0);
        }
      }, link.title);
    }));
  };

  var socialButtonsEl = function socialButtonsEl(socialButtons, piwikEvents) {
    return /*#__PURE__*/React.createElement(SocialButtonContainer, null, /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", null, "Follow us")), Object.keys(socialButtons).map(function (type) {
      return /*#__PURE__*/React.createElement(SocialButtons, {
        type: type,
        href: socialButtons[type],
        key: type,
        onClick: function onClick() {
          event(piwikEvents && piwikEvents.socialButton ? piwikEvents.socialButton : ['click', 'footer-item', type + "-page-view"]);
          window.scroll(0, 0);
        }
      });
    }));
  };

  return /*#__PURE__*/React.createElement(CustomizedFooter, {
    textColor: props.textColor,
    font: props.font,
    backgroundColor: props.backgroundColor
  }, hasSocialButtons && linksEl(props.links, props.piwikEvents), /*#__PURE__*/React.createElement(SocialLogoContainer, {
    hasStackedLogo: props.hasStackedLogo
  }, hasSocialButtons ? socialButtonsEl(props.socialButtons, props.piwikEvents) : linksEl(props.links, props.piwikEvents), props.hasStackedLogo ? /*#__PURE__*/React.createElement(StackedContainer, null, /*#__PURE__*/React.createElement("img", {
    alt: "logo",
    src: props.productLogo
  }), /*#__PURE__*/React.createElement(CopyrightText, null, "\xA9 ", props.productName + " " + new Date().getFullYear())) : /*#__PURE__*/React.createElement(FaviconContainer, null, /*#__PURE__*/React.createElement(LogoFavicon, {
    src: props.productLogo
  }), /*#__PURE__*/React.createElement(CopyrightText, null, "\xA9 ", props.productName + " " + new Date().getFullYear()))));
};

StyledFooter.propTypes = {
  /** Product's name */
  productName: PropTypes.string.isRequired,

  /** Product's favicon logo */
  productLogo: PropTypes.string.isRequired,

  /** Text color for footer */
  textColor: PropTypes.string,

  /** Background color for footer */
  backgroundColor: PropTypes.string,

  /** Font for footer */
  font: PropTypes.string,

  /** An array of links that will be used for each menu item. The array should hold multiple objects in the form of {to: '', title:''} where `to` holds the route and `title` holds menu item label. Each item defaults to being an applink (inside of the application); however, if you would like to use an external link then add `external: true` to the object at hand. */
  links: PropTypes.array,

  /** Object which dictates which social buttons will be shown. The object should be formatted with the social media type (facebook, intstagram, medium, pinterest, shop & twitter) as the key and the outlink url as the value. */
  socialButtons: PropTypes.object,

  /** Optional custom event that is logged for each footer on-click. Should be structured as an object like: `{link: [], socialButton: []}` */
  piwikEvents: PropTypes.object,

  /** Option to override the traditional favicon container and create a stacked footer */
  hasStackedLogo: PropTypes.bool
};
export default StyledFooter;